// src/utils/composants.ts — SCE Issues
// La liste des composants (NVS, désignation, code catégorie) : ~15 000 lignes
// de l'ERP, dans le classeur « ListeNVS.xlsx » du dossier « Déclarations NC-S ›
// Liste NVS » (tableau Excel `ListeNVS`, onglet « Liste article »). Dans la
// déclaration NC-S, on y cherche le composant par son NVS ou sa désignation ;
// un composant qui n'y est pas y est AJOUTÉ à l'envoi (06/10, choix de
// Guillaume : pas de liste SharePoint dédiée ; si l'écriture échoue, tant pis).
//
// Pur : aucun React, aucun réseau.

import { chargerJSZip, echapperXml } from '@/utils/xlsxCellules'
import { feuillesDuClasseur, lire, lireTableau } from '@/utils/xlsxLecture'

export const ONGLET_COMPOSANTS = 'Liste article'
const COLONNES = { nvs: 'N°', designation: 'Désignation', categorie: 'Code catégorie article' } as const

/** `[NVS, désignation, catégorie]` — compact : 15 000 lignes gardées dans le navigateur. */
export type Composant = readonly [nvs: string, designation: string, categorie: string]

/** Les composants du classeur. */
export async function lireListeComposants(octets: ArrayBuffer | Uint8Array): Promise<Composant[]> {
  const zip = await (await chargerJSZip()).loadAsync(octets)
  const lignes = await lireTableau(zip, ONGLET_COMPOSANTS, Object.values(COLONNES))
  return lignes.flatMap((l): Composant[] => {
    const nvs = String(l[COLONNES.nvs] ?? '').trim()
    return nvs ? [[nvs, String(l[COLONNES.designation] ?? '').trim(), String(l[COLONNES.categorie] ?? '').trim()]] : []
  })
}

// ─── Recherche ───

function simplifier(t: string): string {
  return t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

/** « nvs 39496 », « NVS-39496 », « 39496 » → « nvs39496 » / « 39496 ». */
function cleNvs(t: string): string {
  return simplifier(t).replace(/[\s-]+/g, '')
}

/**
 * Les composants qui correspondent à la saisie (NVS ou désignation), les plus
 * probables d'abord. Rien tant que la saisie se limite à « NVS ».
 * - Un NVS (« NVS3949 », « 3949 ») : ceux dont le N° commence par la saisie,
 *   puis ceux qui la contiennent.
 * - Des mots : ceux dont la désignation contient TOUS les mots (sans casse
 *   ni accents), ceux qui commencent par le premier mot d'abord.
 */
export function rechercherComposants(liste: readonly Composant[], saisie: string, n = 8): Composant[] {
  const q = saisie.trim()
  const cle = cleNvs(q)
  if (cle.length < 2 || cle === 'nvs') return []

  const commeNvs = /^(nvs)?\d+[a-z]*$/.test(cle)
  if (commeNvs) {
    const chiffres = cle.replace(/^nvs/, '')
    const debut: Composant[] = []
    const dedans: Composant[] = []
    for (const c of liste) {
      const k = cleNvs(c[0]).replace(/^nvs/, '')
      if (k.startsWith(chiffres)) debut.push(c)
      else if (chiffres.length >= 3 && k.includes(chiffres)) dedans.push(c)
      if (debut.length >= n) break
    }
    return [...debut, ...dedans].slice(0, n)
  }

  const mots = simplifier(q).split(/\s+/).filter(Boolean)
  const premier = mots[0] ?? ''
  const tete: Composant[] = []
  const reste: Composant[] = []
  for (const c of liste) {
    const d = simplifier(c[1])
    if (!mots.every((m) => d.includes(m))) continue
    if (d.startsWith(premier)) tete.push(c)
    else reste.push(c)
    if (tete.length >= n) break
  }
  return [...tete, ...reste].slice(0, n)
}

/** Le composant de ce NVS, s'il est connu. */
export function composantDuNvs(liste: readonly Composant[], nvs: string): Composant | undefined {
  const cle = cleNvs(nvs)
  if (!cle || cle === 'nvs') return undefined
  return liste.find((c) => cleNvs(c[0]) === cle)
}

/** Un NVS bien formé, qui vaut la peine d'être ajouté à la liste : `NVS` + chiffres. */
export function nvsAjoutable(nvs: string): boolean {
  return /^NVS\d{3,}[A-Z]*$/i.test(nvs.trim().replace(/\s+/g, ''))
}

// ─── Ajout d'une ligne au classeur ───

/**
 * Le classeur avec une ligne de plus à la fin du tableau `ListeNVS` (le
 * tableau, son filtre et la dimension de la feuille suivent). Le reste est
 * recopié tel quel.
 * @throws Error si le classeur n'a plus la forme attendue.
 */
export async function ajouterComposantAuClasseur(
  octets: ArrayBuffer | Uint8Array,
  composant: { nvs: string; designation: string; categorie?: string },
): Promise<ArrayBuffer> {
  const zip = await (await chargerJSZip()).loadAsync(octets)
  const feuille = (await feuillesDuClasseur(zip)).find((f) => f.nom.trim() === ONGLET_COMPOSANTS)
  if (!feuille?.chemin) throw new Error(`Liste NVS : onglet « ${ONGLET_COMPOSANTS} » introuvable`)

  let xml = await lire(zip, feuille.chemin)
  const lignes = [...xml.matchAll(/<row r="(\d+)"/g)].map((m) => Number(m[1]))
  const nouvelle = Math.max(0, ...lignes) + 1
  const cellule = (col: string, texte: string): string =>
    texte ? `<c r="${col}${nouvelle}" t="inlineStr"><is><t xml:space="preserve">${echapperXml(texte)}</t></is></c>` : ''
  const ligne =
    `<row r="${nouvelle}" spans="1:3">` +
    cellule('A', composant.nvs.trim().toUpperCase()) +
    cellule('B', composant.designation.trim()) +
    cellule('C', (composant.categorie ?? '').trim()) +
    '</row>'
  if (!xml.includes('</sheetData>')) throw new Error('Liste NVS : feuille illisible')
  xml = xml
    .replace('</sheetData>', `${ligne}</sheetData>`)
    .replace(/(<dimension ref="[A-Z]+\d+:[A-Z]+)\d+("\s*\/>)/, `$1${nouvelle}$2`)
  zip.file(feuille.chemin, xml)

  // Le tableau Excel (et son filtre) s'étend jusqu'à la nouvelle ligne.
  const i = feuille.chemin.lastIndexOf('/')
  const rels = zip.file(`${feuille.chemin.slice(0, i)}/_rels/${feuille.chemin.slice(i + 1)}.rels`)
  const cible = rels ? /Target="([^"]*tables\/table\d+\.xml)"/.exec(await rels.async('string'))?.[1] : undefined
  if (cible) {
    const chemin = `xl/tables/${cible.split('/').pop()}`
    const tableau = await lire(zip, chemin)
    zip.file(chemin, tableau.replace(/(ref="[A-Z]+\d+:[A-Z]+)\d+"/g, `$1${nouvelle}"`))
  }
  return zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' })
}
